import { action, observable } from 'mobx';
import { ProjectsStore } from './ProjectsStore';
import { appConfig } from 'cd-eft/config/AppConfig';
import { GraphicsLayer } from 'cd-eft/utils/esriTypes';
import { SpatialReference, MapView } from 'cd-eft/utils/esriTypes';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Layer from '@arcgis/core/layers/Layer';
import { LandLevelingResponse } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import { putStructureRepOnTop } from 'cd-eft/utils/gisUtils';
import * as structureLayerUtils from 'cd-eft/utils/structureLayerUtils';
import { INotification } from 'cd-eft/models/Stores/DesignStore';

interface IboundaryPoint {
  pointName: string;
  x: number;
  y: number;
  z: number;
}
class LandLevelingDesignProjectsStore extends ProjectsStore {
  constructor(private landlevelingdesignstore: any) {
    super(landlevelingdesignstore);
  }
}

export class LandLevelingDesignStore {
  constructor(private appStore: any) {
    this.projectsStore = new LandLevelingDesignProjectsStore(this);
    this.init();
  }

  @observable projectsStore: LandLevelingDesignProjectsStore;
  @observable hasGrid: boolean = false;
  @observable hasDesign: boolean = false;
  @observable editDesign: boolean = false;
  @observable newLandLeveling!: INewLandLeveling;
  @observable newDesignForm!: INewLandLevelingForm;
  @observable chooseCenter: boolean = false;
  @observable newLandLevelingResults?: INewLandLevelingResults = undefined;
  @observable landLevelingSummaryTable: ILandLevelingSummaryTable[] = [];
  @observable projectMetadata;
  @observable selectedLandLevelingsInfo: ISelectedLandLevelsInfo[] = [];
  @observable displayedLandLevelingsIds: number[] = [];
  @observable latestElementId?: number;
  @observable showLoader: boolean = false;
  @observable surveyListDataGridTabs = ['Land Leveling Summary', 'New Land Leveling'];
  @observable currentTab: string = 'Land Leveling Summary';
  @observable newDesign: boolean = false;
  @observable boundaryPoints: IboundaryPoint[] = [];
  @observable landLevelingBoundary: GraphicsLayer = new GraphicsLayer();
  @observable landLevelingLayers: Layer[] = [];
  @observable currentDesignLayer: GroupLayer = new GroupLayer();
  @observable originalDesignLayer: GroupLayer | undefined;
  @observable originalLandLevelingResults: any = {};
  @observable originalIdString: string = '';
  @observable boundaryLayer: GroupLayer = new GroupLayer();
  @observable pendingEdits: IPendingEdits = { name: false, design: false, unsavedEdits: true };
  @observable notification: INotification = {
    message: '',
    header: '',
    type: '',
    hidden: true,
    timestamp: undefined,
  };

  @action changeTab(tab) {
    this.currentTab = tab;
  }

  @action reset() {
    this.init();
  }

  @action.bound showNotification(msg: string, header: string, type: string = 'error') {
    this.notification = {
      message: msg,
      header: header,
      type: type,
      hidden: false,
      timestamp: new Date(),
    };
  }

  @action.bound clearNotification() {
    this.notification = { message: '', header: '', type: '', hidden: true, timestamp: undefined };
  }

  @action init() {
    this.resetNewLandLevelingForm();
    this.landLevelingSummaryTable = [];
    this.selectedLandLevelingsInfo = [];
    this.displayedLandLevelingsIds = [];
    this.chooseCenter = false;
    this.hasDesign = false;
    this.editDesign = false;
    this.showLoader = false;
    this.newDesign = false;
    this.currentTab = 'Land Leveling Summary';
    this.newLandLeveling = {
      landLevelingName: '',
    };

    this.projectMetadata = {
      projectName: '',
      surfaceName: '',
      surfaceType: '',
    };
    this.hasGrid = false;
    this.landLevelingLayers = [];
  }

  @action resetNewLandLevelingForm() {
    this.newDesignForm = {
      name: '',
      type: 'Land Leveling',
      centroid: false,
      basePoint: {
        pointName: 'center',
        x: 0,
        y: 0,
        z: 0,
      },
      bestFit: false,
      xslope: 0,
      yslope: 0,
      borrow: 0,
      waste: 0,
      cutFillRatio: 1,
      llStakeoutGrid: 100,
      bufferSize: 0,
      useWholeField: false,
      designElementId: 0,
      boundaryPoints: [],
    };
    this.pendingEdits = {
      name: false,
      design: false,
      unsavedEdits: true
    };
  }

  @action cancelDesignElement() {
    this.changeTab('Land Leveling Summary');
    this.newLandLevelingResults = undefined;
    this.hasDesign = false;
    this.editDesign = false;
    this.newDesign = false;
    this.originalDesignLayer = undefined;
    this.originalLandLevelingResults = undefined;
    this.originalIdString = '';
    this.resetNewLandLevelingForm();
  }

  @action.bound removeAllLandLevelingLayers(view: MapView) {
    while (this.landLevelingLayers.length > 0) {
      const landLevelingLayer = this.landLevelingLayers.shift();
      if (landLevelingLayer) {
        view!.map.remove(landLevelingLayer);
      }
    }
    this.landLevelingLayers = [];
  }

  @action displayLandLevelingGraphic(
    view: MapView,
    landLeveling: LandLevelingResponse,
    isCurrentDesignLayer: boolean,
    projectSR: SpatialReference,
    id?: string
  ) {
    const map = view.map;

    const landLevelSpatialReference = projectSR;
    if (map) {
      const landLevelingGroupLayer = structureLayerUtils.constructLandLevelingLayer(
        landLeveling,
        landLevelSpatialReference,
        view.spatialReference,
        'intl-feet',
        'meters',
        id
      );
      if (id) {
        landLevelingGroupLayer.id = id;
      }
      // this.constructLandLevelingPlaneLayer(
      //   landLeveling,
      //   landLevelSpatialReference,
      //   view.spatialReference,
      //   'intl-feet',
      //   'meters',
      //   'Modified Plane Points'
      // );
      map.add(landLevelingGroupLayer);

      if (isCurrentDesignLayer) {
        this.currentDesignLayer = landLevelingGroupLayer;
        if (id) {
          const entry = this.landLevelingSummaryTable.find((entry) => entry.designElementId.toString() === id);
          const layerIndex = this.landLevelingLayers.findIndex((layer) => layer.id === id.toString());
          if (layerIndex !== -1) {
            this.landLevelingLayers.splice(layerIndex, 1);
          }
          if (entry && entry.checked) this.landLevelingLayers.push(landLevelingGroupLayer);
        }
      } else {
        this.landLevelingLayers.push(landLevelingGroupLayer);
      }
      putStructureRepOnTop(view, landLeveling.name);
    }
  }
}

// LandLevelingDesignStore Interfaces
interface INewLandLeveling {
  landLevelingName: string;
}

interface INewLandLevelingForm {
  name: string;
  type: string;
  centroid?: boolean;
  basePoint: ICenterObject;
  bestFit: boolean;
  xslope: number;
  yslope: number;
  borrow: number;
  waste: number;
  cutFillRatio: number;
  designElementId: number;
  useWholeField: boolean;
  boundaryPoints: ICenterObject[];
  llStakeoutGrid: number;
  bufferSize: number;
}

interface ICenterObject {
  pointName: string;
  x: number;
  y: number;
  z: number;
}

interface responseDataObject {
  id: number;
  pointName: string;
  x: number;
  y: number;
  z: number;
  visiable: boolean;
  tinnable: boolean;
  description: any;
  objectName: any;
  keyString: any;
}

interface INewLandLevelingResults {
  name: string;
  type: string;
  basePoint: responseDataObject;
  xslope: number;
  yslope: number;
  useWholeField: boolean;
  borrow: number;
  waste: number;
  cutFillRatio: number;
  cutVolume: number;
  fillVolume: number;
  cutRate: number;
  fieldArea: number;
  bufferArea: number;
  totalBufferCut: number;
  totalBufferFill: number;
  designElementId: number;
  boundaryPoints: responseDataObject[];
  modifiedPlanePoints: responseDataObject[];
  // wkid: string;
}

interface designElementTypeIdObject {
  id: number;
  name: string;
}

interface ISelectedLandLevelsInfo {
  designElementId: number;
  designElementName: string;
  designElementTypeId: designElementTypeIdObject;
  sessionId: string;
  surveyName: string;
}

interface ILandLevelingSummaryTable {
  designElementId: number;
  designElementName: string;
  designElementTypeId: {
    id: number;
    name: string;
  };
  sessionId: string;
  surveyName: string;
  checked?: boolean;
}

interface IPendingEdits {
  name: boolean;
  design: boolean;
  unsavedEdits: boolean
}

